import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as liveEventsMapApi from './MerchantLiveEventsMap.api';
import {
    LiveEventsMapFilter,
    LiveEventsMapStats,
    LocationData,
    LiveEventAreaItem,
    EventPerformanceRow,
    LiveEventOption,
    LiveEventLocationOption,
} from './MerchantLiveEventsMap.data';

export interface LiveEventsMapState {
    stats: LiveEventsMapStats | null;
    mapLocations: LocationData[];
    activityLevels: LiveEventAreaItem[];
    eventPerformanceRows: EventPerformanceRow[];
    eventOptions: LiveEventOption[];
    locationOptions: LiveEventLocationOption[];
    filter: LiveEventsMapFilter;
    loading: {
        stats: boolean;
        map: boolean;
        activityLevels: boolean;
        eventPerformance: boolean;
        eventOptions: boolean;
        locationOptions: boolean;
    };
    error: string | null;
}

const defaultFilter: LiveEventsMapFilter = {
    event_ids: [],
    location_ids: [],
    demographic_age: '',
    quick_filter_days: 30,
};

const initialState: LiveEventsMapState = {
    stats: null,
    mapLocations: [],
    activityLevels: [],
    eventPerformanceRows: [],
    eventOptions: [],
    locationOptions: [],
    filter: defaultFilter,
    loading: {
        stats: false,
        map: false,
        activityLevels: false,
        eventPerformance: false,
        eventOptions: false,
        locationOptions: false,
    },
    error: null,
};

export const fetchLiveEventsMapStats = createAsyncThunk(
    'liveEventsMap/fetchStats',
    async (_, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventsMapStats();
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load event stats');
        }
    }
);

export const fetchLiveEventsMapMap = createAsyncThunk(
    'liveEventsMap/fetchMap',
    async (filter: LiveEventsMapFilter, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventsMapMap(filter);
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load map data');
        }
    }
);

export const fetchLiveEventsMapActivityLevels = createAsyncThunk(
    'liveEventsMap/fetchActivityLevels',
    async (filter: LiveEventsMapFilter, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventsMapActivityLevels(filter);
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load activity levels');
        }
    }
);

export const fetchLiveEventsMapEventPerformance = createAsyncThunk(
    'liveEventsMap/fetchEventPerformance',
    async (filter: LiveEventsMapFilter, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventsMapEventPerformanceTable(filter);
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load event performance');
        }
    }
);

export const fetchLiveEventOptions = createAsyncThunk(
    'liveEventsMap/fetchEventOptions',
    async (_, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventOptions();
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load events');
        }
    }
);

export const fetchLiveEventLocationOptions = createAsyncThunk(
    'liveEventsMap/fetchLocationOptions',
    async (_, { rejectWithValue }) => {
        try {
            return await liveEventsMapApi.getLiveEventLocationOptions();
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load locations');
        }
    }
);

/** Fetch all live events map data with current filter (stats + map + activity + table) */
export const fetchLiveEventsMapAll = createAsyncThunk(
    'liveEventsMap/fetchAll',
    async (filter: LiveEventsMapFilter, { dispatch, rejectWithValue }) => {
        try {
            await Promise.all([
                dispatch(fetchLiveEventsMapStats()).unwrap().catch(() => null),
                dispatch(fetchLiveEventsMapMap(filter)).unwrap().catch(() => []),
                dispatch(fetchLiveEventsMapActivityLevels(filter)).unwrap().catch(() => []),
                dispatch(fetchLiveEventsMapEventPerformance(filter)).unwrap().catch(() => []),
            ]);
        } catch (e: unknown) {
            return rejectWithValue(e instanceof Error ? e.message : 'Failed to load live events map');
        }
    }
);

const slice = createSlice({
    name: 'liveEventsMap',
    initialState,
    reducers: {
        setLiveEventsMapFilter: (state, action: { payload: Partial<LiveEventsMapFilter> }) => {
            state.filter = { ...state.filter, ...action.payload };
        },
        clearLiveEventsMapError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Stats
        builder
            .addCase(fetchLiveEventsMapStats.pending, (s) => { s.loading.stats = true; s.error = null; })
            .addCase(fetchLiveEventsMapStats.fulfilled, (s, a) => { s.loading.stats = false; s.stats = a.payload; })
            .addCase(fetchLiveEventsMapStats.rejected, (s, a) => { s.loading.stats = false; s.error = (a.payload as string) ?? null; });
        // Map
        builder
            .addCase(fetchLiveEventsMapMap.pending, (s) => { s.loading.map = true; })
            .addCase(fetchLiveEventsMapMap.fulfilled, (s, a) => { s.loading.map = false; s.mapLocations = a.payload; })
            .addCase(fetchLiveEventsMapMap.rejected, (s) => { s.loading.map = false; });
        // Activity levels
        builder
            .addCase(fetchLiveEventsMapActivityLevels.pending, (s) => { s.loading.activityLevels = true; })
            .addCase(fetchLiveEventsMapActivityLevels.fulfilled, (s, a) => { s.loading.activityLevels = false; s.activityLevels = a.payload; })
            .addCase(fetchLiveEventsMapActivityLevels.rejected, (s) => { s.loading.activityLevels = false; });
        // Event performance
        builder
            .addCase(fetchLiveEventsMapEventPerformance.pending, (s) => { s.loading.eventPerformance = true; })
            .addCase(fetchLiveEventsMapEventPerformance.fulfilled, (s, a) => { s.loading.eventPerformance = false; s.eventPerformanceRows = a.payload; })
            .addCase(fetchLiveEventsMapEventPerformance.rejected, (s) => { s.loading.eventPerformance = false; });
        // Event options
        builder
            .addCase(fetchLiveEventOptions.pending, (s) => { s.loading.eventOptions = true; })
            .addCase(fetchLiveEventOptions.fulfilled, (s, a) => { s.loading.eventOptions = false; s.eventOptions = a.payload; })
            .addCase(fetchLiveEventOptions.rejected, (s) => { s.loading.eventOptions = false; });
        // Location options
        builder
            .addCase(fetchLiveEventLocationOptions.pending, (s) => { s.loading.locationOptions = true; })
            .addCase(fetchLiveEventLocationOptions.fulfilled, (s, a) => { s.loading.locationOptions = false; s.locationOptions = a.payload; })
            .addCase(fetchLiveEventLocationOptions.rejected, (s) => { s.loading.locationOptions = false; });
        // Fetch all
        builder
            .addCase(fetchLiveEventsMapAll.pending, (s) => { s.error = null; })
            .addCase(fetchLiveEventsMapAll.rejected, (s, a) => { s.error = (a.payload as string) ?? null; });
    },
});

export const { setLiveEventsMapFilter, clearLiveEventsMapError } = slice.actions;
export default slice.reducer;
